import { FC, ReactNode } from "react";
import MuiBreadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export interface BreadcrumbItemConfig {
  label: string;
  onClick?: () => void;
  isActive?: boolean;
  isLoading?: boolean;
  hasDropdown?: boolean;
  onDropdownClick?: (event: React.MouseEvent<SVGSVGElement>) => void;
}

export interface BreadcrumbProps {
  items: BreadcrumbItemConfig[];
  separator?: ReactNode;
}

interface BreadcrumbItemProps {
  label: string;
  onClick?: () => void;
  isActive?: boolean;
  isLoading?: boolean;
  hasDropdown?: boolean;
  onDropdownClick?: (event: React.MouseEvent<SVGSVGElement>) => void;
}

const BreadcrumbItem: FC<BreadcrumbItemProps> = ({
  label,
  onClick,
  isActive = false,
  isLoading = false,
  hasDropdown = false,
  onDropdownClick,
}) => {
  if (isLoading) {
    return <Skeleton width={100} height={16} />;
  }

  const handleDropdownClick = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    onDropdownClick?.(e);
  };

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
      }}
    >
      <Typography
        component="span"
        fontSize="11px"
        fontWeight={300}
        color={isActive ? "#367286" : "#757575"}
        letterSpacing="0.5px"
        sx={{
          cursor: onClick ? "pointer" : "default",
          "&:hover": onClick
            ? {
                textDecoration: "underline",
              }
            : {},
        }}
        onClick={onClick}
      >
        {label}
      </Typography>
      {hasDropdown && (
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 14,
            color: "#757575",
            cursor: "pointer",
            transition: "color 0.15s ease-in-out",
            "&:hover": {
              color: "#367286",
            },
          }}
          onClick={handleDropdownClick}
        />
      )}
    </Box>
  );
};

export const Breadcrumb: FC<BreadcrumbProps> = ({ items, separator = "/" }) => {
  return (
    <MuiBreadcrumbs
      separator={
        <Typography
          component="span"
          fontSize="11px"
          fontWeight={300}
          color="#757575"
          sx={{ mx: 0.5 }}
        >
          {separator}
        </Typography>
      }
    >
      {items.map((item, index) => (
        <BreadcrumbItem
          key={index}
          label={item.label}
          onClick={item.onClick}
          isActive={item.isActive}
          isLoading={item.isLoading}
          hasDropdown={item.hasDropdown}
          onDropdownClick={item.onDropdownClick}
        />
      ))}
    </MuiBreadcrumbs>
  );
};

export default Breadcrumb;
